/**
 * ObservabilityPage — Sprint 5.8 (Observabilidade).
 *
 * Aba "Observabilidade" no NOC — visão operacional ISP.
 *  - Cards: HB/min · Online · Offline · Inventory · Health · OS ·
 *    Audit.
 *  - Resumo das métricas que o /metrics expõe ao Prometheus.
 *
 * O Grafana (dashboard ipwatchdog-field-ops) é a visão histórica;
 * esta aba é a visão ao-vivo, com polling de 15s.
 */
import { useCallback, useEffect, useState } from "react";
import { observabilitySummary } from "../lib/api.js";
import { t } from "../i18n/index.js";

const POLL_MS = 15000;

export default function ObservabilityPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback((signal) => {
    observabilitySummary({ signal })
      .then((d) => { setData(d); setErr(null); })
      .catch((e) => {
        if (e?.name !== "AbortError") setErr(e?.message || String(e));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    load(ac.signal);
    const id = setInterval(() => {
      const pollAc = new AbortController();
      load(pollAc.signal);
    }, POLL_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load]);

  const d = data || {};

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="section-label mb-2">Telemetria da plataforma</div>
          <h1 className="text-3xl font-semibold tracking-tight text-noc-text">
            {t("observability.title")}
          </h1>
          <p className="text-sm text-noc-muted mt-1">
            Visão consolidada de atividade, disponibilidade e eventos do NOC.
          </p>
        </div>
        <span className="text-xs text-noc-muted border border-noc-border bg-noc-panel rounded-full px-3 py-1.5">
          {t("observability.autoHint")}
        </span>
      </header>

      {err && (
        <div className="text-xs text-noc-err bg-noc-err/10 border
                        border-noc-err/30 rounded p-2">
          {err}
        </div>
      )}

      {/* Cards principais */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <BigCard label={t("observability.cardHbRate")}
                 value={loading ? "…" : (d.hb_per_min ?? 0)}
                 suffix="/min" tone="cyan" />
        <BigCard label={t("observability.cardOnline")}
                 value={loading ? "…" : (d.devices_online ?? 0)}
                 tone="emerald" />
        <BigCard label={t("observability.cardOffline")}
                 value={loading ? "…" : (d.devices_offline ?? 0)}
                 tone="red" />
        <BigCard label={t("observability.cardDevicesTotal")}
                 value={loading ? "…" : (d.devices_total ?? 0)}
                 tone="blue" />
      </div>

      {/* Cards de operação */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <BigCard label={t("observability.cardInventory")}
                 value={loading ? "…" : (d.inventory_total ?? 0)}
                 tone="purple" />
        <BigCard label={t("observability.cardHealthWarning")}
                 value={loading ? "…" : (d.health_warning ?? 0)}
                 tone="amber" />
        <BigCard label={t("observability.cardHealthCritical")}
                 value={loading ? "…" : (d.health_critical ?? 0)}
                 tone="red" />
        <BigCard label={t("observability.cardWorkOrders")}
                 value={loading ? "…" : (d.work_orders_open ?? 0)}
                 tone="sky" />
      </div>

      {/* Cards de auditoria */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <BigCard label={t("observability.cardAutoBind")}
                 value={loading ? "…" : (d.auto_bind_total ?? 0)}
                 tone="cyan" />
        <BigCard label={t("observability.cardTemplates")}
                 value={loading ? "…" : (d.templates_applied ?? 0)}
                 tone="purple" />
        <BigCard label={t("observability.cardAudit")}
                 value={loading ? "…" : (d.audit_total ?? 0)}
                 tone="blue" />
      </div>

      {/* Nota sobre Grafana */}
      <div className="glass-card p-4 text-xs text-noc-muted">
        {t("observability.grafanaHint")}
        <code className="text-noc-accent ml-1">/metrics</code>
      </div>
    </div>
  );
}

function BigCard({ label, value, suffix, tone }) {
  const toneCls = {
    cyan: "border-noc-accent/30 text-noc-accent",
    emerald: "border-noc-ok/30 text-noc-ok",
    red: "border-noc-err/30 text-noc-err",
    blue: "border-noc-accent/30 text-noc-accent",
    purple: "border-noc-purple/30 text-noc-purple",
    amber: "border-noc-warn/30 text-noc-warn",
    sky: "border-noc-accent/30 text-noc-accent",
  }[tone] || "border-noc-border text-noc-text";
  return (
    <div className={`stat-card ${toneCls}`}>
      <div className="text-2xl font-semibold mt-2">
        {value}
        {suffix && (
          <span className="text-xs text-noc-muted ml-1">{suffix}</span>
        )}
      </div>
      <div className="section-label">{label}</div>
    </div>
  );
}
